import { Controller, useFieldArray } from 'react-hook-form';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import * as Yup from 'yup';

import {
    BillingCycle,
    BillingTiming,
    CurrencyCode,
    FeeScheduleSaveRequest,
    FeeScheduleWithUsage,
    FlatFeeConfig,
    GUID,
    Tenant,
} from '@ndvr/billing-api';

import { NumberInput } from '@/components/NumberInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { FormInput } from '@/components/ui/input';
import { FormSelect, SelectOption } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { titleCase } from 'utils';
import { cn } from 'utils/cn';
import { useFormWithYup } from 'utils/hooks/useFormWithYup';

import { useCreateFeeSchedule, useUpdateFeeSchedule } from './fee-schedule-queries';

const schema = Yup.object({
    feeScheduleName: Yup.string().required('Required'),
    feeScheduleDescription: Yup.string(),
    billingTimingOption: Yup.object({ value: Yup.string().required(), label: Yup.string().required() }).required(),
    billingCycleOption: Yup.object({ value: Yup.string().required(), label: Yup.string().required() }).required(),
    billAtSingleBps: Yup.boolean().required(),
    tiers: Yup.array()
        .of(
            Yup.object({
                tierStart: Yup.number()
                    .transform((v, orig) => (orig === '' ? NaN : v))
                    .typeError('Required')
                    .required('Required')
                    .min(0, 'Cannot be negative'),
                annualBps: Yup.number()
                    .transform((v, orig) => (orig === '' ? NaN : v))
                    .typeError('Required')
                    .required('Required')
                    .min(0, 'Cannot be negative')
                    .test('max-2-decimals', 'Max 2 decimal places', (v, ctx) =>
                        ctx.originalValue === '' || ctx.originalValue == null
                            ? true
                            : /^\d+(\.\d{1,2})?$/.test(String(ctx.originalValue)),
                    ),
            }),
        )
        .min(1, 'At least one tier is required')
        .test('unique-tier-starts', 'Tier start values must be unique', function (tiers) {
            if (!tiers) return true;
            const starts = tiers.map((t) => t.tierStart);
            const errors = starts
                .map((start, i) =>
                    start != null && !isNaN(start) && starts.indexOf(start) !== i
                        ? this.createError({
                              path: `${this.path}[${i}].tierStart`,
                              message: 'Tier start values must be unique',
                          })
                        : null,
                )
                .filter((e): e is Yup.ValidationError => e !== null);
            return errors.length ? new Yup.ValidationError(errors) : true;
        })
        .required(),
    useFlatFee: Yup.boolean().required(),
    flatFeeAmount: Yup.number()
        .transform((v, orig) => (orig === '' ? undefined : v))
        .when('useFlatFee', {
            is: true,
            then: (s) =>
                s
                    .typeError('Required')
                    .required('Required')
                    .min(0, 'Cannot be negative')
                    .integer('Must be a whole number'),
            otherwise: (s) => s.notRequired(),
        }),
});

const BILLING_TIMING_OPTIONS: SelectOption[] = Object.values(BillingTiming).map((v) => ({
    value: v,
    label: titleCase(v),
}));

const BILLING_CYCLE_OPTIONS: SelectOption[] = Object.values(BillingCycle).map((v) => ({
    value: v,
    label: titleCase(v),
}));

type Tier = { tierStart: number | string; annualBps: number | string };

type FormValues = {
    feeScheduleName: string;
    feeScheduleDescription: string;
    billingTimingOption: SelectOption;
    billingCycleOption: SelectOption;
    billAtSingleBps: boolean;
    tiers: Tier[];
    useFlatFee: boolean;
    flatFeeAmount: number | string;
};

interface CreateFeeScheduleDialogProps {
    tenantGUID: GUID<Tenant>;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    feeSchedule?: FeeScheduleWithUsage;
}

const defaultFormValues = (feeSchedule?: FeeScheduleWithUsage): FormValues => ({
    feeScheduleName: feeSchedule?.feeScheduleName ?? '',
    feeScheduleDescription: feeSchedule?.feeScheduleDescription ?? '',
    billingTimingOption: {
        value: feeSchedule?.billingTiming ?? BillingTiming.ARREARS,
        label: titleCase(feeSchedule?.billingTiming ?? BillingTiming.ARREARS),
    },
    billingCycleOption: {
        value: feeSchedule?.billingCycle ?? BillingCycle.QUARTERLY,
        label: titleCase(feeSchedule?.billingCycle ?? BillingCycle.QUARTERLY),
    },
    billAtSingleBps: feeSchedule?.billAtSingleBps ?? false,
    tiers: feeSchedule?.feeTiers.map((t) => ({
        tierStart: Number(t.tierStart),
        annualBps: Number(t.annualBps),
    })) ?? [{ tierStart: 0, annualBps: 0 }],
    useFlatFee: !!feeSchedule?.flatFeeConfig,
    flatFeeAmount: feeSchedule?.flatFeeConfig ? Number(feeSchedule.flatFeeConfig.annualFlatFeeAmount) : '',
});

export const CreateFeeScheduleDialog = ({
    tenantGUID,
    open,
    onOpenChange,
    feeSchedule,
}: CreateFeeScheduleDialogProps) => {
    const isEdit = !!feeSchedule;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[760px]" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Fee Schedule' : 'Create Fee Schedule'}</DialogTitle>
                </DialogHeader>

                {open && (
                    <FeeScheduleForm
                        tenantGUID={tenantGUID}
                        feeSchedule={feeSchedule}
                        onClose={() => onOpenChange(false)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

interface FeeScheduleFormProps {
    tenantGUID: GUID<Tenant>;
    feeSchedule?: FeeScheduleWithUsage;
    onClose: () => void;
}

const FeeScheduleForm = ({ tenantGUID, feeSchedule, onClose }: FeeScheduleFormProps) => {
    const isEdit = !!feeSchedule;
    const createMutation = useCreateFeeSchedule(tenantGUID);
    const updateMutation = useUpdateFeeSchedule();
    const isPending = createMutation.isPending || updateMutation.isPending;

    const form = useFormWithYup<FormValues>(schema, undefined, {
        defaultValues: defaultFormValues(feeSchedule),
    });

    const { fields, append, remove } = useFieldArray({ control: form.control, name: 'tiers' });
    const tierErrors = form.formState.errors.tiers;
    const useFlatFee = form.watch('useFlatFee');
    const flatFeeErrors = form.formState.errors;

    const onSubmit = async (data: FormValues) => {
        const flatFeeConfig: FlatFeeConfig | undefined = data.useFlatFee
            ? {
                  annualFlatFeeAmount: String(data.flatFeeAmount),
                  annualFlatFeeCurrency: CurrencyCode.USD,
              }
            : undefined;

        const request: FeeScheduleSaveRequest = {
            feeScheduleName: data.feeScheduleName,
            feeScheduleDescription: data.feeScheduleDescription || undefined,
            billingTiming: data.billingTimingOption.value as BillingTiming,
            billingCycle: data.billingCycleOption.value as BillingCycle,
            billAtSingleBps: data.billAtSingleBps,
            feeTiers: data.tiers.map((t) => ({ tierStart: String(t.tierStart), annualBps: String(t.annualBps) })),
            flatFeeConfig,
        };

        if (isEdit) {
            await updateMutation.mutateAsync({ feeScheduleGUID: feeSchedule.feeScheduleGUID, request });
        } else {
            await createMutation.mutateAsync(request);
        }
        onClose();
    };

    return (
        <Form {...form}>
            <form className="flex min-h-0 flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-x-6 gap-y-4 overflow-y-auto pr-1 md:grid-cols-2">
                    <div className="flex flex-col gap-4">
                        <FormInput formMethods={form} name="feeScheduleName" label="Name*" required />
                        <FormInput formMethods={form} name="feeScheduleDescription" label="Description" />

                        <div className="grid grid-cols-2 gap-4">
                            <FormSelect
                                formMethods={form}
                                name="billingTimingOption"
                                label="Billing Timing*"
                                options={BILLING_TIMING_OPTIONS}
                                rules={{ required: true }}
                            />
                            <FormSelect
                                formMethods={form}
                                name="billingCycleOption"
                                label="Billing Cycle*"
                                options={BILLING_CYCLE_OPTIONS}
                                rules={{ required: true }}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="billAtSingleBps"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-y-0 space-x-2">
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormLabel className="cursor-pointer font-normal">Bill at single bps</FormLabel>
                                </FormItem>
                            )}
                        />

                        <Separator />

                        <FormField
                            control={form.control}
                            name="useFlatFee"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-y-0 space-x-2">
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormLabel className="cursor-pointer font-normal">
                                        Charge an annual flat fee
                                    </FormLabel>
                                </FormItem>
                            )}
                        />

                        <div className={cn('flex flex-col gap-1', !useFlatFee && 'invisible')}>
                            <FormLabel>Annual Flat Fee (USD)*</FormLabel>
                            <Controller
                                control={form.control}
                                name="flatFeeAmount"
                                render={({ field: { value, onChange } }) => (
                                    <NumberInput
                                        value={value}
                                        onValueChange={(v) => onChange(v.value === '' ? '' : Number(v.value))}
                                        thousandSeparator
                                        prefix="$"
                                        allowNegative={false}
                                        decimalScale={0}
                                        className="border-input bg-background flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                                    />
                                )}
                            />
                            {flatFeeErrors.flatFeeAmount && (
                                <p className="text-destructive text-xs">{flatFeeErrors.flatFeeAmount.message}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <FormLabel>Fee Tiers*</FormLabel>
                        <div className="flex gap-2">
                            <span className="text-muted-foreground flex-1 text-xs font-medium">Tier Start ($)</span>
                            <span className="text-muted-foreground flex-1 text-xs font-medium">Annual bps</span>
                            <span className="w-8 shrink-0" />
                        </div>
                        <div className="flex flex-col gap-2">
                            {fields.map((field, index) => (
                                <div key={field.id} className="flex items-start gap-2">
                                    <div className="flex-1">
                                        <Controller
                                            control={form.control}
                                            name={`tiers.${index}.tierStart`}
                                            render={({ field: { value, onChange } }) => (
                                                <NumberInput
                                                    value={value}
                                                    onValueChange={(v) =>
                                                        onChange(v.value === '' ? '' : Number(v.value))
                                                    }
                                                    thousandSeparator
                                                    prefix="$"
                                                    allowNegative={false}
                                                    disabled={index === 0}
                                                    className="border-input bg-background flex h-9 w-full rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                                                />
                                            )}
                                        />
                                        {tierErrors?.[index]?.tierStart && (
                                            <p className="text-destructive mt-1 text-xs">
                                                {tierErrors[index]?.tierStart?.message}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <Controller
                                            control={form.control}
                                            name={`tiers.${index}.annualBps`}
                                            render={({ field: { value, onChange } }) => (
                                                <NumberInput
                                                    value={value}
                                                    onValueChange={(v) =>
                                                        onChange(v.value === '' ? '' : Number(v.value))
                                                    }
                                                    allowNegative={false}
                                                    decimalScale={2}
                                                    className="border-input bg-background flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                                                />
                                            )}
                                        />
                                        {tierErrors?.[index]?.annualBps && (
                                            <p className="text-destructive mt-1 text-xs">
                                                {tierErrors[index]?.annualBps?.message}
                                            </p>
                                        )}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="mb-0.5 shrink-0"
                                        disabled={fields.length === 1}
                                        onClick={() => remove(index)}
                                    >
                                        <LuTrash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        {tierErrors?.message && <p className="text-destructive text-xs">{tierErrors.message}</p>}
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="self-start"
                            onClick={() => append({ tierStart: '', annualBps: '' })}
                        >
                            <LuPlus className="h-4 w-4" /> Add tier
                        </Button>
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isPending}>
                        {isPending ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create'}
                    </Button>
                </DialogFooter>
            </form>
        </Form>
    );
};
